import React from 'react'
import { List, ListItem, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import moment from '@e3dc-react/shell/Libs/moment'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AuthenticationInfoV1, ChargingSessionModelV1 } from 'src/Libs/e-mobility/charging-sessions'
import { formatDuration } from '@e3dc-react/shell/Datetime'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { PricingInfo } from './PricingInfo'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import EdisonConnectPricing from './EdisonConnectPricing'
import { DISCHARGE_CAPABLE_WALLBOX_TYPES } from 'src/Libs/wallboxes/consts'

const useStyles = makeStyles()(theme => ({
  listItem: {
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: 0,
  },
  listItemTitle: {
    overflowWrap: 'anywhere',
    hyphens: 'auto',
    width: '33%',
  },
  listItemValue: {
    width: '67%',
    paddingLeft: theme.spacing(1),
    overflowWrap: 'anywhere',
    hyphens: 'auto',
  },
}))

export interface EMobilityChargingSessionDetailModalPropsV1 {
  open: boolean
  onClose: () => void
  place: number
  session?: ChargingSessionModelV1
  wallboxes: WallboxHardwareModel[]
}

const WAT_TO_KWH = 1000

export const EMobilityChargingSessionDetailsModalV1: React.VFC<EMobilityChargingSessionDetailModalPropsV1> = ({
  open,
  place,
  onClose,
  session,
  wallboxes,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  if (!session) {
    onClose()
    return null
  }

  const wallbox = wallboxes.find(wallbox => wallbox.wallboxId === session.wallbox.id)
  const wallboxIndex = wallbox?.number
  const isDischargeCapable = !!wallbox?.wallboxType && DISCHARGE_CAPABLE_WALLBOX_TYPES.includes(wallbox?.wallboxType)
  const totalCost = session.dynamic ? session.chargePriceTotal : session.chargePrice * (session.energyAll / WAT_TO_KWH)

  return (
    <Dialog
      title={t('emobility.chargingSession.chargingSession', { sessionID: session.sessionID })}
      onClose={onClose}
      place={place}
      actions={{
        secondary: {
          name: t('common.close'),
        },
      }}
      maxWidth="sm"
    >
      <List>
        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.wallbox')}
          </Typography>
          <Typography variant="body2" className={classes.listItemValue}>
            {session.wallbox.name}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.status')}
          </Typography>
          <Typography variant="body2" className={classes.listItemValue}>
            {t(`emobility.chargingSession.chargingSessionStatus.${session.status}`)}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.timerange')}
          </Typography>
          <Typography variant="body2" className={classes.listItemValue}>
            {`${moment.utc(session.startAt).local().format(t('common.format.dateTime'))} - ${
              session.stopAt ? moment.utc(session.stopAt).local().format(t('common.format.dateTime')) : 'N/A'
            }`}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.created')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {moment.utc(session.createdAt).local().format(t('common.format.dateTime'))}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.updated')}
          </Typography>
          <Typography variant="body2" className={classes.listItemValue}>
            {moment.utc(session.updatedAt).local().format(t('common.format.dateTime'))}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.authentication')}
          </Typography>
          <Typography variant="body2" component="div" className={classes.listItemValue}>
            <AuthenticationInfoV1 authType={session.authentication.type} rfid={session.authentication.rfid} />
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.loaded')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {`${t('emobility.chargingSession.chargingGrid')} ${t('common.format.kwh', {
              value: getLocalizedNumber((session.energyAll - session.energySolar) / WAT_TO_KWH),
            })} • ${t('emobility.chargingSession.chargingSolar')} ${t('common.format.kwh', {
              value: getLocalizedNumber(session.energySolar / WAT_TO_KWH),
            })} • ${t('emobility.chargingSession.chargingAll')} ${t('common.format.kwh', {
              value: getLocalizedNumber(session.energyAll / WAT_TO_KWH),
            })}`}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.chargeDuration')}
          </Typography>
          <Typography variant="body2" className={classes.listItemValue}>
            {`${t('emobility.chargingSession.active')} ${formatDuration(session.chargeTimeActive)} • ${t(
              'emobility.chargingSession.inactive'
            )} ${formatDuration(session.chargeTimeInactive)}`}
          </Typography>
        </ListItem>

        <ListItem divider={isDischargeCapable} className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.price')}
          </Typography>
          <PricingInfo totalCostKWh={totalCost} dynamicPrice={session.dynamic} className={classes.listItemValue} />
        </ListItem>

        {isDischargeCapable && <EdisonConnectPricing session={session} wallboxIndex={wallboxIndex} />}
      </List>
    </Dialog>
  )
}
